import type {
  CrowdexCollaboration,
  CrowdexReferenceKind,
  CrowdexUIMessage,
} from '@crowdex/agent-contracts';
import { useCallback, useEffect, useRef } from 'react';
import { useAgentRuntime } from '@/hooks/use-agent-runtime';
import { useCrowdexChat } from '@/hooks/use-crowdex-chat';
import { createCrowdexChat } from '@/lib/agent-runtime/chat';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import type { AnswerToolApproval } from '@/lib/conversations/message-part/tool-approval';
import { isLocalMachine } from '@/lib/local-runtime';
import { endNativeBrowserTurn, type NativeAgent } from '@/lib/native-desktop';
import { latestReplyModelId, latestSessionConfigOptions } from '@/lib/session-events';

function conversationRuntimeIdentity(options: {
  conversationId: string;
  agent: NativeAgent | null;
  workspaceRoot: string | null;
  logicalWorkspaceId?: string;
  cloudRuntime?: CloudRuntime | null;
}): string {
  return JSON.stringify({
    conversationId: options.conversationId,
    workspaceRoot: options.workspaceRoot,
    logicalWorkspaceId: options.logicalWorkspaceId,
    agent:
      options.agent === null
        ? null
        : {
            id: options.agent.id,
            command: options.agent.command,
            args: options.agent.args,
            env: options.agent.env,
          },
    cloud:
      options.cloudRuntime === null || options.cloudRuntime === undefined
        ? null
        : { id: options.cloudRuntime.id, url: options.cloudRuntime.url },
  });
}

/** Stops both the browser stream reader and the server-owned generation. */
export function useStopGeneration(
  stop: () => void,
  cancelGeneration: () => Promise<void>,
  onStop?: () => void | Promise<void>,
): () => Promise<void> {
  return useCallback(async () => {
    await Promise.allSettled([
      Promise.resolve().then(stop),
      cancelGeneration(),
      Promise.resolve().then(onStop),
    ]);
  }, [cancelGeneration, onStop, stop]);
}

/**
 * The per-conversation runtime shared by every surface: one server-owned AI SDK agent and chat,
 * plus the derived `busy` flag. Keeping this pairing in a single hook means the
 * main pane, split panes, and the side chat all start a conversation the same
 * way and only differ in the persistence/composer they layer on top.
 */
export function useConversationRuntime(options: {
  conversationId: string;
  agent: NativeAgent | null;
  workspaceRoot: string | null;
  cloudRuntime?: CloudRuntime | null;
  restoredCollaboration?: CrowdexCollaboration | null;
  preferredModelId?: string | null;
  initialMessages?: CrowdexUIMessage[];
  persistenceTitle?: string;
  persistenceWorkspace?: {
    name: string;
    runtime: 'local' | 'worktree';
    origin?: string;
  } | null;
  resolveReference?: (kind: CrowdexReferenceKind, id: string) => Promise<unknown>;
}) {
  const cloud = options.cloudRuntime !== null && options.cloudRuntime !== undefined;
  const connection = useAgentRuntime({
    conversationId: options.conversationId,
    agent: options.agent,
    workspaceRoot: options.workspaceRoot,
    cloudRuntime: options.cloudRuntime ?? null,
    // The server persists the turns, in the cloud and locally.
    serverPersisted: cloud || isLocalMachine(),
    ...(options.persistenceWorkspace === undefined
      ? {}
      : { persistenceWorkspace: options.persistenceWorkspace }),
    restoredCollaboration: options.restoredCollaboration ?? null,
    preferredModelId: options.preferredModelId ?? null,
    resolveReference: options.resolveReference,
    ...(options.persistenceTitle === undefined
      ? {}
      : { persistenceTitle: options.persistenceTitle }),
  });
  const identity = conversationRuntimeIdentity(options);
  const chatRef = useRef<{
    identity: string;
    conversationId: string;
    transport: typeof connection.transport;
    connection: typeof connection;
    chat: ReturnType<typeof createCrowdexChat>;
    configBoundaryMessageId: string | null;
  } | null>(null);
  if (
    chatRef.current === null ||
    chatRef.current.conversationId !== options.conversationId ||
    ((chatRef.current.identity !== identity ||
      chatRef.current.transport !== connection.transport) &&
      chatRef.current.chat.status !== 'submitted' &&
      chatRef.current.chat.status !== 'streaming')
  ) {
    const preservedMessages =
      chatRef.current !== null && chatRef.current.conversationId === options.conversationId
        ? chatRef.current.chat.messages.length > 0
          ? chatRef.current.chat.messages
          : options.initialMessages
        : options.initialMessages;
    const runtimeChanged = chatRef.current !== null && chatRef.current.identity !== identity;
    const previousBoundary = chatRef.current?.configBoundaryMessageId ?? null;
    chatRef.current = {
      identity,
      conversationId: options.conversationId,
      transport: connection.transport,
      connection,
      configBoundaryMessageId: runtimeChanged
        ? (preservedMessages?.at(-1)?.id ?? null)
        : previousBoundary,
      chat: createCrowdexChat({
        id: options.conversationId,
        ...(preservedMessages === undefined ? {} : { messages: [...preservedMessages] }),
        transport: connection.transport,
        executeClientTool: connection.executeClientTool,
        submitClientToolResult: async (result) =>
          await connection.respondToInteraction({ type: 'tool-result', ...result }),
        onFinish: ({ isAbort, isDisconnect, isError }) => {
          void endNativeBrowserTurn(
            options.conversationId,
            !isAbort && !isDisconnect && !isError,
          ).catch(() => undefined);
        },
        onError: () => {
          void endNativeBrowserTurn(options.conversationId).catch(() => undefined);
        },
      }),
    };
  }
  const chat = useCrowdexChat(
    chatRef.current.chat,
    options.cloudRuntime,
    cloud || isLocalMachine(),
  );
  useEffect(() => {
    const boundaryId = chatRef.current?.configBoundaryMessageId ?? null;
    const boundaryIndex =
      boundaryId === null ? -1 : chat.messages.findIndex((message) => message.id === boundaryId);
    const current = chat.messages.slice(boundaryIndex + 1);
    const configOptions = latestSessionConfigOptions(current);
    if (configOptions !== null) connection.setReportedConfigOptions(configOptions);
    connection.setReplyModelId(latestReplyModelId(current));
  }, [chat.messages, connection.setReportedConfigOptions, connection.setReplyModelId]);
  const turnConnection = chatRef.current.connection;
  const stopGeneration = useStopGeneration(chat.stop, turnConnection.cancelGeneration, () =>
    endNativeBrowserTurn(options.conversationId),
  );
  const addToolApprovalResponse: AnswerToolApproval = async ({ remember, ...input }) => {
    await turnConnection.respondToInteraction({
      type: 'approval',
      id: input.id,
      approved: input.approved,
      ...(remember === true ? { remember } : {}),
    });
    await chat.addToolApprovalResponse(input);
  };
  const busy = chat.status === 'submitted' || chat.status === 'streaming';
  return { connection, chat: { ...chat, addToolApprovalResponse }, busy, stopGeneration };
}
